import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { PageContainer } from '@/components/ui/page-container';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAnalytics } from '../api/hooks';
import { StatusChart } from './StatusChart';

const PERIODS = ['7d', '30d', '90d', 'ytd', 'all'] as const;

const PERIOD_KEYS: Record<string, string> = {
  '7d': 'analytics.period7d',
  '30d': 'analytics.period30d',
  '90d': 'analytics.period90d',
  ytd: 'analytics.periodYtd',
  all: 'analytics.periodAll',
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatPercent(value: number): string {
  return `${(Math.round(value * 10) / 10).toFixed(1)}%`;
}

interface KpiCardProps {
  title: string;
  value: string;
  subtitle: string;
  isLoading: boolean;
  isError: boolean;
}

function KpiCard({ title, value, subtitle, isLoading, isError }: Readonly<KpiCardProps>) {
  if (isLoading) {
    return (
      <Card data-testid="kpi-skeleton">
        <CardHeader className="pb-2">
          <div className="h-4 w-24 animate-pulse rounded bg-muted" />
        </CardHeader>
        <CardContent>
          <div className="h-8 w-32 animate-pulse rounded bg-muted" />
          <div className="mt-2 h-3 w-40 animate-pulse rounded bg-muted" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-2xl font-bold">{isError ? '—' : value}</p>
        <p className="mt-1 text-xs text-muted-foreground">{isError ? '—' : subtitle}</p>
      </CardContent>
    </Card>
  );
}

interface AnalyticsViewProps {
  getAccessToken: () => Promise<string>;
}

export function AnalyticsView({ getAccessToken }: Readonly<AnalyticsViewProps>) {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<string>('30d');
  const { data, isLoading, isError } = useAnalytics(getAccessToken, period);

  useEffect(() => {
    if (isError) {
      toast.error(t('analytics.error'));
    }
  }, [isError, t]);

  const isEmpty = data?.total_quotes === 0;

  return (
    <PageContainer>
      <h2 className="mb-4 text-lg font-semibold">{t('analytics.title')}</h2>

      {/* Period Selector */}
      <div className="mb-6 flex gap-2">
        {PERIODS.map((p) => (
          <Button
            key={p}
            size="sm"
            variant={period === p ? 'default' : 'outline'}
            data-active={period === p}
            onClick={() => setPeriod(p)}
          >
            {t(PERIOD_KEYS[p])}
          </Button>
        ))}
      </div>

      {/* Empty State */}
      {isEmpty && <p className="py-8 text-center text-muted-foreground">{t('analytics.empty')}</p>}

      {/* KPI Cards */}
      {!isEmpty && (
        <div className="grid gap-4 lg:grid-cols-2">
          <KpiCard
            title={t('analytics.totalRevenue')}
            value={data ? formatCurrency(data.total_revenue) : ''}
            subtitle={
              data ? t('analytics.totalRevenueSubtitle', { count: data.accepted_quotes }) : ''
            }
            isLoading={isLoading}
            isError={isError}
          />
          <KpiCard
            title={t('analytics.quoteCount')}
            value={data ? String(data.total_quotes) : ''}
            subtitle={
              data
                ? t('analytics.quoteCountSubtitle', {
                    draft: data.status_breakdown.draft,
                    sent: data.status_breakdown.sent,
                    accepted: data.status_breakdown.accepted,
                    rejected: data.status_breakdown.rejected,
                  })
                : ''
            }
            isLoading={isLoading}
            isError={isError}
          />
          <KpiCard
            title={t('analytics.avgJobValue')}
            value={data ? formatCurrency(data.avg_job_value) : ''}
            subtitle={t('analytics.avgJobValueSubtitle')}
            isLoading={isLoading}
            isError={isError}
          />
          <KpiCard
            title={t('analytics.closeRate')}
            value={data ? formatPercent(data.close_rate) : ''}
            subtitle={
              data
                ? t('analytics.closeRateSubtitle', {
                    accepted: data.accepted_quotes,
                    total: data.total_quotes,
                  })
                : ''
            }
            isLoading={isLoading}
            isError={isError}
          />
        </div>
      )}

      {/* Status Distribution Chart */}
      {(isLoading || (data && !isEmpty)) && (
        <div className="mt-6">
          <StatusChart data={data?.status_breakdown} isLoading={isLoading} />
        </div>
      )}
    </PageContainer>
  );
}
